import { useState, useEffect, useRef, useCallback, Children } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import { getAdminWithdrawals } from "../services/adminWithdrawalService";
import { getPayments } from "../services/paymentService";
import { UserModal } from "../components/Gamesuser/UserModal";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../utils/permissions";
// logo served from public folder
const logo = "assets/images/LogoNew.png";

/* ── Sidebar category label ── */
const SidebarLabel = ({ label }) => (
  <li className="sidebar-menu-group-title" style={sidebarStyles.categoryLabel}>
    <span>{label}</span>
  </li>
);

/* ── Sidebar item (no dropdown) ── */
// `permission` / `anyOf` hide the item when the user lacks access.
const SidebarItem = ({ to, icon, label, permission, anyOf }) => {
  const { hasPermission, hasAny } = useAuth();
  if (permission && !hasPermission(permission)) return null;
  if (anyOf && !hasAny(anyOf)) return null;
  return (
    <li>
      <NavLink
        to={to}
        title={label}
        className={({ isActive }) => isActive ? "sidebar-link active-link" : "sidebar-link"}
        style={({ isActive }) => ({
          ...sidebarStyles.link,
          ...(isActive ? sidebarStyles.linkActive : {}),
        })}
      >
        <Icon icon={icon} style={sidebarStyles.linkIcon} />
        <span>{label}</span>
      </NavLink>
    </li>
  );
};

/* ── Sidebar dropdown group ──
   Hidden automatically if every child SubItem has been filtered out. */
const SidebarGroup = ({ icon, label, children }) => {
  const [open, setOpen] = useState(false);

  // Drop null children so an empty group auto-hides.
  const visible = Children.toArray(children).filter((c) => c !== null && c !== false);
  if (visible.length === 0) return null;

  return (
    <li className={open ? "dropdown dropdown-open" : "dropdown"} style={{ listStyle: "none" }}>
      <button
        title={label}
        onClick={() => setOpen(!open)}
        style={{ ...sidebarStyles.link, ...sidebarStyles.groupBtn }}
      >
        <Icon icon={icon} style={sidebarStyles.linkIcon} />
        <span className="sidebar-group-label" style={{ flex: 1, textAlign: "left" }}>{label}</span>
        <span className="sidebar-arrow" style={{
          display: "inline-flex",
          fontSize: 12,
          transition: "transform 0.25s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          color: "#94a3b8",
        }}>
          <Icon icon="solar:alt-arrow-down-bold" />
        </span>
      </button>
      <ul
        className="sidebar-submenu"
        style={{
          ...sidebarStyles.submenu,
          maxHeight: open ? "600px" : "0",
          overflow: "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        {visible}
      </ul>
    </li>
  );
};

/* ── Sidebar sub-item ── */
const SubItem = ({ to, icon, label, permission, anyOf }) => {
  const { hasPermission, hasAny } = useAuth();
  if (permission && !hasPermission(permission)) return null;
  if (anyOf && !hasAny(anyOf)) return null;
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) => isActive ? "active-sub" : ""}
        style={({ isActive }) => ({
          ...sidebarStyles.subLink,
          ...(isActive ? sidebarStyles.subLinkActive : {}),
        })}
      >
        {icon
          ? <Icon icon={icon} style={{ fontSize: 14, flexShrink: 0, color: "inherit" }} />
          : <span style={sidebarStyles.dot} />
        }
        <span>{label}</span>
      </NavLink>
    </li>
  );
};

const MasterLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canAddUser = hasPermission("users.create");
  const canSeeDeposits = hasPermission("payments.view");
  const canSeeWithdrawals = hasPermission("withdrawals.view");

  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const prevPendingRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    try {
      // eslint-disable-next-line no-undef
      const AudioCtx = window.AudioContext || window["webkitAudioContext"];
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.28, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.45);
    } catch (_) {}
  }, []);

  const fetchPendingCounts = useCallback(async () => {
    try {
      const [wRes, dRes] = await Promise.all([
        getAdminWithdrawals("pending"),
        getPayments(),
      ]);
      const wCount = wRes?.status ? (wRes.data?.length || 0) : 0;
      const dCount = dRes?.success
        ? (dRes.data || []).filter((r) => r.status === "requested").length
        : 0;

      setPendingWithdrawals(wCount);
      setPendingDeposits(dCount);

      const total = wCount + dCount;
      const prev = prevPendingRef.current;
      if (total > 0 && (prev === null || total > prev)) {
        playNotificationSound();
      }
      prevPendingRef.current = total;
    } catch (_) {}
  }, [playNotificationSound]);

  useEffect(() => {
    fetchPendingCounts();
    const id = setInterval(fetchPendingCounts, 10000);
    return () => clearInterval(id);
  }, [fetchPendingCounts]);

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>

      {/* ══════════ SIDEBAR ══════════ */}
      <aside
        className={
          sidebarActive ? "sidebar active"
          : mobileMenu ? "sidebar sidebar-open"
          : "sidebar"
        }
        style={sidebarStyles.sidebar}
      >
        {/* Close btn (mobile) */}
        <button
          onClick={() => setMobileMenu(false)}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>

        {/* Logo */}
        <div style={sidebarStyles.logoArea}>
          <Link to="/" style={sidebarStyles.logoLink}>
            <img src={logo} alt="Opinion Games" style={sidebarStyles.logoImg} />
          </Link>
        </div>

        {/* Menu */}
        <div className="sidebar-menu-area" style={sidebarStyles.menuArea}>
          <ul className="sidebar-menu" style={sidebarStyles.menuList}>

            {/* ── OVERVIEW ── */}
            <SidebarLabel label="Overview" />
            <SidebarItem to="/" icon="solar:home-smile-angle-bold-duotone" label="Dashboard" permission="dashboard.view" />

            {/* ── USER MANAGEMENT ── */}
            <SidebarLabel label="User Management" />
            <SidebarGroup icon="solar:users-group-two-rounded-bold-duotone" label="Users">
              <SubItem to="/users-list" icon="solar:user-bold-duotone" label="User List" permission="users.view" />
              <SubItem to="/demo-user-list" icon="solar:ghost-bold-duotone" label="Demo User List" permission="demo_users.view" />
              <SubItem to="/user-360-report" icon="solar:user-id-bold-duotone" label="User 360 Report" permission="user_360.view" />
              <SubItem to="/user-bets-report" icon="solar:gamepad-bold-duotone" label="User Bets Report" permission="user_bets.view" />
            </SidebarGroup>

            {/* ── FINANCE ── */}
            <SidebarLabel label="Finance" />
            <SidebarGroup icon="solar:wallet-money-bold-duotone" label="User Wallet">
              <SubItem to="/user-point-list" icon="solar:wallet-bold-duotone" label="User Points" permission="user_points.view" />
              <SubItem to="/withdrawals-list" icon="solar:transfer-horizontal-bold-duotone" label="Withdrawals" permission="withdrawals.view" />
              <SubItem to="/bonus-management" icon="solar:gift-bold-duotone" label="Bonus Management" permission="bonus.view" />
              <SubItem to="/upi-manage" icon="solar:card-bold-duotone" label="UPI Manage" permission="upi.view" />
              <SubItem to="/payment-list" icon="solar:bill-list-bold-duotone" label="Deposit" permission="payments.view" />
            </SidebarGroup>

            {/* ── SECTIONS ── */}
            <SidebarLabel label="Sections" />
            <SidebarGroup icon="solar:layers-bold-duotone" label="Questions">
              <SubItem to="/Add-all-section" icon="solar:add-square-bold-duotone" label="Add All Sections" permission="sections.add_all" />
              <SubItem to="/section-a-list" icon="solar:letter-bold-duotone" label="Section A List" permission="section_a.list" />
              <SubItem to="/section-b-list" icon="solar:letter-bold-duotone" label="Section B List" permission="section_b.list" />
              <SubItem to="/section-c-list" icon="solar:letter-bold-duotone" label="Section C List" permission="section_c.list" />
            </SidebarGroup>
            <SidebarGroup icon="solar:checklist-bold-duotone" label="Section A">
              <SubItem to="/asections-list" icon="solar:question-square-bold-duotone" label="A Section Questions" permission="section_a.questions" />
              <SubItem to="/match-result" icon="solar:cup-bold-duotone" label="Match Result" permission="section_a.match_result" />
              <SubItem to="/asections-questions-result" icon="solar:add-circle-bold-duotone" label="Add Question Result" permission="section_a.result_add" />
              <SubItem to="/asections-questions-result-list" icon="solar:list-bold-duotone" label="Result List" permission="section_a.result_list" />
            </SidebarGroup>
            <SidebarGroup icon="solar:chart-2-bold-duotone" label="Section B">
              <SubItem to="/add-games-points" icon="solar:add-circle-bold-duotone" label="Add Games Points" permission="section_b.points_add" />
              <SubItem to="/games-points-list" icon="solar:list-bold-duotone" label="Games Points List" permission="section_b.points_list" />
              <SubItem to="/add-points-result" icon="solar:add-circle-bold-duotone" label="Add Points Result" permission="section_b.result_add" />
              <SubItem to="/points-result-list" icon="solar:list-bold-duotone" label="Points Result List" permission="section_b.result_list" />
            </SidebarGroup>
            <SidebarGroup icon="solar:question-circle-bold-duotone" label="Section C">
              <SubItem to="/add-questiongame" icon="solar:add-circle-bold-duotone" label="Add Question Game" permission="section_c.question_add" />
              <SubItem to="/questiongame-list" icon="solar:list-bold-duotone" label="Question Game List" permission="section_c.question_list" />
              <SubItem to="/add-gameresult" icon="solar:add-circle-bold-duotone" label="Add Game Result" permission="section_c.result_add" />
              <SubItem to="/gameresult-list" icon="solar:list-bold-duotone" label="Game Result List" permission="section_c.result_list" />
            </SidebarGroup>

            {/* ── REPORTS ── */}
            <SidebarLabel label="Reports" />
            <SidebarGroup icon="solar:document-text-bold-duotone" label="Reports">
              <SubItem to="/all-reports" icon="solar:file-text-bold-duotone" label="All Reports" permission="reports.all" />
              <SubItem to="/all-user-reports" icon="solar:users-group-two-rounded-bold-duotone" label="All User Reports" permission="reports.all_user" />
              <SubItem to="/match-bets-report" icon="solar:chart-square-bold-duotone" label="Match Bets Report" permission="reports.match_bets" />
              <SubItem to="/user-bets-report" icon="solar:user-speak-bold-duotone" label="User Bets Report" permission="reports.user_bets" />
              <SubItem to="/section-a-reports" icon="solar:checklist-bold-duotone" label="Section A Reports" permission="reports.section_a" />
              <SubItem to="/section-b-reports" icon="solar:chart-2-bold-duotone" label="Section B Reports" permission="reports.section_b" />
              <SubItem to="/section-c-reports" icon="solar:question-circle-bold-duotone" label="Section C Reports" permission="reports.section_c" />
            </SidebarGroup>

            {/* ── NOTIFICATIONS ── */}
            <SidebarLabel label="Notifications" />
            <SidebarGroup icon="solar:bell-bold-duotone" label="Notifications">
              <SubItem to="/notifications" icon="solar:bell-bing-bold-duotone" label="Manage Notifications" permission="notifications.view" />
            </SidebarGroup>

            {/* ── MEDIA & SETTINGS ── */}
            <SidebarLabel label="Media & Settings" />
            <SidebarGroup icon="solar:gallery-bold-duotone" label="Banner">
              <SubItem to="/ads-list" icon="solar:list-bold-duotone" label="All Ads" permission="ads.view" />
              <SubItem to="/add-ad" icon="solar:add-circle-bold-duotone" label="Add New Ad" permission="ads.manage" />
            </SidebarGroup>
            <SidebarGroup icon="solar:box-bold-duotone" label="Packages">
              <SubItem to="/add-package" icon="solar:add-circle-bold-duotone" label="Add Package" permission="packages.manage" />
              <SubItem to="/package-list" icon="solar:list-bold-duotone" label="Package List" permission="packages.view" />
            </SidebarGroup>
            <SidebarGroup icon="solar:gamepad-bold-duotone" label="Game Setup">
              <SubItem to="/league-list" icon="solar:cup-star-bold-duotone" label="League Management" permission="leagues.manage" />
              <SubItem to="/team-list" icon="solar:users-group-rounded-bold-duotone" label="Team Management" permission="teams.manage" />
              <SubItem to="/match-list" icon="solar:flag-2-bold-duotone" label="Match Management" permission="matches.manage" />
            </SidebarGroup>

            {/* ── ADMIN & ROLES ── */}
            <SidebarLabel label="Admin & Roles" />
            <SidebarGroup icon="solar:shield-user-bold-duotone" label="Access Control">
              <SubItem to="/admin-users" icon="solar:users-group-rounded-bold-duotone" label="Admin Users" permission="admin_users.view" />
              <SubItem to="/role-management" icon="solar:key-bold-duotone" label="Role Management" permission="roles.view" />
            </SidebarGroup>

          </ul>
        </div>
      </aside>

      {/* ══════════ MAIN ══════════ */}
      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>

        {/* ── TOP NAVBAR ── */}
        <div style={navStyles.navbar}>
          <div style={navStyles.navLeft}>
            {/* Desktop sidebar collapse toggle */}
            <button style={navStyles.iconBtn} onClick={() => setSidebarActive(!sidebarActive)} type="button"
              className="sidebar-toggle d-none d-lg-flex">
              <Icon icon={sidebarActive ? "solar:sidebar-minimalistic-bold" : "heroicons:bars-3-solid"} style={{ fontSize: 20, color: "#fff" }} />
            </button>
            {/* Mobile menu toggle — hidden on desktop */}
            <button style={navStyles.iconBtn} onClick={() => setMobileMenu(!mobileMenu)} type="button"
              className="d-flex d-lg-none">
              <Icon icon="heroicons:bars-3-solid" style={{ fontSize: 20, color: "#fff" }} />
            </button>

            {/* Search — hidden on mobile */}
            <div style={navStyles.searchWrap} className="d-none d-md-flex">
              <Icon icon="ion:search-outline" style={navStyles.searchIcon} />
              <input style={navStyles.searchInput} type="text" placeholder="Search anything..." />
            </div>
          </div>

          <div style={navStyles.navRight}>
            {/* ── SHORTCUT BUTTONS ── */}
            <div style={navStyles.shortcuts}>
              {canAddUser && (
                <button
                  onClick={() => setShowAddUser(true)}
                  style={{ ...navStyles.shortcutBtn, background: "rgba(255,255,255,0.15)" }}
                  title="Add New User"
                >
                  <Icon icon="solar:user-plus-bold" style={{ fontSize: 15 }} />
                  <span className="d-none d-sm-inline" style={navStyles.shortcutLabel}>Add User</span>
                </button>
              )}
              {canSeeDeposits && (
                <button
                  onClick={() => navigate("/payment-list")}
                  style={{ ...navStyles.shortcutBtn, background: "rgba(74,222,128,0.2)", position: "relative", overflow: "visible" }}
                  title="New Deposit"
                >
                  <Icon icon="solar:wallet-money-bold" style={{ fontSize: 15 }} />
                  <span className="d-none d-sm-inline" style={navStyles.shortcutLabel}>Deposit</span>
                  {pendingDeposits > 0 && (
                    <span style={navStyles.badge}>
                      {pendingDeposits > 99 ? "99+" : pendingDeposits}
                    </span>
                  )}
                </button>
              )}
              {canSeeWithdrawals && (
                <button
                  onClick={() => navigate("/withdrawals-list")}
                  style={{ ...navStyles.shortcutBtn, background: "rgba(251,146,60,0.2)", position: "relative", overflow: "visible" }}
                  title="Withdrawals"
                >
                  <Icon icon="solar:transfer-horizontal-bold" style={{ fontSize: 15 }} />
                  <span className="d-none d-sm-inline" style={navStyles.shortcutLabel}>Withdrawal</span>
                  {pendingWithdrawals > 0 && (
                    <span style={navStyles.badge}>
                      {pendingWithdrawals > 99 ? "99+" : pendingWithdrawals}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Divider — hidden on mobile */}
            <div style={navStyles.vDivider} className="d-none d-sm-block" />

            {/* Profile dropdown */}
            <div className="dropdown">
              <button
                style={navStyles.profileBtn}
                type="button"
                data-bs-toggle="dropdown"
              >
                <img
                  src="assets/images/fav.png"
                  alt="user"
                  style={navStyles.profileImg}
                />
                <div style={navStyles.profileInfo} className="d-none d-sm-flex">
                  <span style={navStyles.profileName}>{user?.name || "Admin"}</span>
                  <span style={navStyles.profileRole}>{ROLE_LABELS[user?.role] || "User"}</span>
                </div>
                <Icon icon="solar:alt-arrow-down-bold" style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} className="d-none d-sm-inline" />
              </button>
              <div className="dropdown-menu to-top dropdown-menu-sm">
                <ul className="to-top-list">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                      onClick={(e) => { e.preventDefault(); logout(); }}
                    >
                      <Icon icon="lucide:power" className="icon text-xl" />
                      Log Out
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ── PAGE BODY ── */}
        <div className="dashboard-main-body">{children}</div>

        <UserModal
          show={showAddUser}
          onHide={() => setShowAddUser(false)}
          editUser={null}
          onSaved={() => {}}
        />

        {/* ── FOOTER ── */}
        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© 2026 Opinion Games. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

/* ── Sidebar styles ── */
const sidebarStyles = {
  sidebar: {
    background: "#ffffff",
    borderRight: "1px solid #f1f5f9",
  },
  logoArea: {
    padding: "0 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    height: 72,
    flexShrink: 0,
    background: "#fff",
  },
  logoLink: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
  },
  logoImg: {
    height: 34,
    maxWidth: 160,
    objectFit: "contain",
  },
  menuArea: {
    padding: "8px 0 24px",
    background: "#fff",
    borderInlineEnd: "none",
  },
  menuList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  categoryLabel: {
    padding: "20px 20px 6px",
    listStyle: "none",
  },
  link: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 20px",
    margin: "1px 10px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#475569",
    textDecoration: "none",
    cursor: "pointer",
    transition: "all 0.18s ease",
    background: "none",
    border: "none",
    width: "calc(100% - 20px)",
    fontFamily: "inherit",
  },
  linkActive: {
    background: "rgba(22, 99, 43, 0.08)",
    color: "#16632b",
    fontWeight: 600,
  },
  linkIcon: {
    fontSize: 18,
    flexShrink: 0,
    color: "inherit",
  },
  groupBtn: {
    width: "calc(100% - 20px)",
  },
  submenu: {
    listStyle: "none",
    padding: "0 0 0 0",
    margin: 0,
  },
  subLink: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 20px 7px 48px",
    fontSize: "0.83rem",
    fontWeight: 500,
    color: "#64748b",
    textDecoration: "none",
    transition: "all 0.15s ease",
  },
  subLinkActive: {
    color: "#16632b",
    fontWeight: 700,
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "currentColor",
    flexShrink: 0,
    opacity: 0.6,
  },
};

/* ── Navbar styles ── */
const navStyles = {
  navbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px",
    height: 60,
    background: "linear-gradient(135deg, #0f4c25 0%, #16632b 60%, #1da144 100%)",
    boxShadow: "0 4px 20px rgba(22, 99, 43, 0.3)",
    flexWrap: "nowrap",
    gap: 8,
    position: "sticky",
    top: 0,
    zIndex: 100,
    overflow: "visible",
  },
  navLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  iconBtn: {
    width: 36,
    height: 36,
    border: "none",
    borderRadius: 8,
    background: "rgba(255,255,255,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.2s",
    flexShrink: 0,
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 10,
    fontSize: 15,
    color: "rgba(255,255,255,0.6)",
    pointerEvents: "none",
  },
  searchInput: {
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 8,
    padding: "7px 14px 7px 32px",
    color: "#fff",
    fontSize: "0.85rem",
    outline: "none",
    width: 200,
    fontFamily: "inherit",
  },
  shortcuts: {
    display: "flex",
    gap: 6,
    overflow: "visible",
  },
  badge: {
    position: "absolute",
    top: -8,
    right: -8,
    background: "#e05252",
    color: "#fff",
    borderRadius: "50%",
    minWidth: 18,
    height: 18,
    fontSize: "0.6rem",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 4px",
    lineHeight: 1,
    boxShadow: "0 2px 6px rgba(224,82,82,0.55)",
    zIndex: 110,
    pointerEvents: "none",
  },
  shortcutBtn: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 8px",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 8,
    color: "#fff",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit",
    backdropFilter: "blur(4px)",
    flexShrink: 0,
  },
  shortcutLabel: {
    display: "inline",
  },
  vDivider: {
    width: 1,
    height: 28,
    background: "rgba(255,255,255,0.2)",
  },
  profileBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 10,
    padding: "5px 10px 5px 5px",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  profileImg: {
    width: 32,
    height: 32,
    borderRadius: 8,
    objectFit: "contain",
    background: "#fff",
    padding: 2,
  },
  profileInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    lineHeight: 1.2,
  },
  profileName: {
    color: "#fff",
    fontSize: "0.82rem",
    fontWeight: 700,
  },
  profileRole: {
    color: "rgba(255,255,255,0.65)",
    fontSize: "0.7rem",
    fontWeight: 500,
  },
};

export default MasterLayout;
